import { PerformanceAudienceComparisonRow } from '@/services/endpoints/performanceAudience';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleAdsIcon, MetaIcon } from '../PlatformBrandIcons';
import { Card, CardHead, CardSubtitle, CardTitle } from '../PerformanceAudienceComponents';

type PerformanceAudienceComparisonTableProps = {
  rows: PerformanceAudienceComparisonRow[];
  onExportCsv?: () => void;
};

export const PerformanceAudienceComparisonTable: FC<PerformanceAudienceComparisonTableProps> = ({ rows }) => {
  const { t } = useTranslation('performanceAudience');

  const renderCell = (cell: PerformanceAudienceComparisonRow['google']) => {
    if (cell.unavailable) {
      return (
        <td className='border-b border-ink-100 px-3.5 py-2.5 text-ink-400 tabular-nums'>
          <span className='text-sm text-ink-300'>—</span> <span className='text-[11.5px]'>{t('comparison.notAvailable')}</span>
        </td>
      );
    }

    return <td className='border-b border-ink-100 px-3.5 py-2.5 font-semibold text-ink-900 tabular-nums'>{cell.value}</td>;
  };

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle text={t('comparison.title')} />
          <CardSubtitle text={t('comparison.subtitle')} />
        </div>
      </CardHead>
      <table className='w-full border-separate border-spacing-0 text-[13.5px]'>
        <thead>
          <tr>
            <th className='rounded-l-lg bg-ink-50 px-3.5 py-2 text-left text-[11.5px] font-semibold uppercase tracking-[0.07em] text-ink-500'>
              {t('comparison.metric')}
            </th>
            <th className='bg-ink-50 px-3.5 py-2 text-left text-[11.5px] font-semibold uppercase tracking-[0.07em] text-ink-500'>
              <span className='inline-flex items-center gap-2'>
                <GoogleAdsIcon />
                {t('platforms.googleAds')}
              </span>
            </th>
            <th className='rounded-r-lg bg-ink-50 px-3.5 py-2 text-left text-[11.5px] font-semibold uppercase tracking-[0.07em] text-ink-500'>
              <span className='inline-flex items-center gap-2'>
                <MetaIcon />
                {t('platforms.meta')}
              </span>
            </th>
          </tr>
        </thead>
        <tbody className='[&>tr:last-child>td]:border-b-0'>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className='border-b border-ink-100 px-3.5 py-2.5 text-ink-700 tabular-nums'>
                {t(`comparison.rows.${row.id}` as 'comparison.rows.impressions')}
              </td>
              {renderCell(row.google)}
              {renderCell(row.meta)}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};
